import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, ChartLineUp, Phone } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { IMAGES, CONTACT } from "../lib/api";

const DETAILS = {
  residential: {
    title: "Residential Solar Installation in Akola",
    tagline: "Home Rooftop Solar",
    image: IMAGES.residential,
    intro: "Install a rooftop solar system on your home in Akola and cut your electricity bill by up to 90%. Under PM Surya Ghar Yojana, get up to ₹78,000 central government subsidy.",
    benefits: [
      "₹78,000 central subsidy (for 3kW+ systems)",
      "Save ₹3,500–₹12,000 per month on electricity",
      "25-year panel warranty · 10-year inverter warranty",
      "Payback period of just 3–4 years",
      "Net metering approved systems",
      "Zero-down EMI options with partner banks",
    ],
    process: [
      { t: "Free Site Visit", d: "Our engineer measures your roof, shade, and reviews 12-month bill." },
      { t: "Custom Quote", d: "Tailored proposal with panel brand, subsidy estimate, and ROI." },
      { t: "Turnkey Install", d: "Installation completed in 3-5 days. DISCOM paperwork handled by us." },
      { t: "Net Meter & Go", d: "MSEDCL inspection, net meter installed, system live." },
    ],
    roi: {
      bill: "₹5,000/month",
      system: "3 kW",
      cost: "₹1,17,000 (after subsidy)",
      saving: "₹54,000/year",
      payback: "~2.5 years",
    },
  },
  commercial: {
    title: "Commercial Solar Solutions",
    tagline: "Business Solar",
    image: IMAGES.commercial,
    intro: "Cut your business electricity cost by 70%+. Whether it's a shop, office, school, or hotel — Orax Solar designs optimal rooftop systems with net metering under commercial tariffs.",
    benefits: [
      "70%+ reduction in monthly electricity bills",
      "Accelerated depreciation tax benefit (40%)",
      "ROI within 3.5–5 years",
      "Net metering / gross metering / captive consumption",
      "Premium mono-PERC or bifacial panels",
      "Remote monitoring & performance analytics",
    ],
    process: [
      { t: "Load Analysis", d: "We study your 12-month commercial bill and KVA demand." },
      { t: "System Design", d: "Engineered for max yield with your existing loads." },
      { t: "Installation", d: "Minimal disruption. Done in 10-15 days." },
      { t: "Commissioning", d: "Net meter + monitoring app goes live." },
    ],
    roi: {
      bill: "₹50,000/month",
      system: "50 kW",
      cost: "₹25L",
      saving: "₹6L/year",
      payback: "~4 years",
    },
  },
  industrial: {
    title: "Industrial Solar Projects",
    tagline: "MW-Scale Solar",
    image: IMAGES.texture,
    intro: "Large industrial solar plants — from 100 kW to several MW. Factories, cold storage, textile mills, rice mills across Vidarbha. Captive, OPEX, or PPA structures available.",
    benefits: [
      "MW-scale ground-mount or rooftop",
      "Captive consumption or third-party PPA",
      "OPEX model with zero capital expenditure",
      "Accelerated depreciation tax benefits",
      "Reduce power cost from ₹9/unit to ~₹3.5/unit",
      "SCADA monitoring & predictive maintenance",
    ],
    process: [
      { t: "Feasibility Study", d: "Engineering + financial + regulatory feasibility report." },
      { t: "PPA / Financial Structure", d: "Choose CAPEX, OPEX, or PPA based on balance sheet." },
      { t: "EPC Execution", d: "End-to-end engineering, procurement, construction." },
      { t: "O&M Contract", d: "25-year performance guarantee with O&M contract." },
    ],
    roi: {
      bill: "₹5L/month",
      system: "500 kW",
      cost: "₹2.5Cr",
      saving: "₹65L/year",
      payback: "~4 years",
    },
  },
  maintenance: {
    title: "Solar Panel Maintenance & AMC",
    tagline: "Service & Support",
    image: IMAGES.worker,
    intro: "Protect your solar investment with professional maintenance. Dirty panels can lose 15-25% output. We service any brand — whether installed by us or others.",
    benefits: [
      "Quarterly professional cleaning",
      "Annual electrical & structural inspection",
      "Priority breakdown response (< 48 hrs)",
      "Performance monitoring & reporting",
      "Inverter troubleshooting & replacement",
      "Panel diagnostics with thermal imaging",
    ],
    process: [
      { t: "Site Audit", d: "Initial audit of existing system health and performance." },
      { t: "AMC Plan", d: "Choose from Basic, Standard, or Premium AMC plans." },
      { t: "Scheduled Service", d: "Cleaning + inspection as per plan. Reports shared via WhatsApp." },
      { t: "Emergency Support", d: "Any breakdown — priority response by local Akola team." },
    ],
    roi: {
      bill: "AMC Basic",
      system: "1–10 kW",
      cost: "₹3,500/year",
      saving: "15-25% more yield",
      payback: "Immediate",
    },
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const d = DETAILS[slug];
  if (!d) return <Navigate to="/services" replace/>;

  return (
    <div data-testid={`service-detail-${slug}`}>
      <SEO page={slug} titleFallback={d.title}/>

      {/* HERO */}
      <section className="pt-28 pb-10">
        <div className="container-custom">
          <Link to="/services" className="text-sm text-[#5C7368] hover:text-[#0A1F15]">← All services</Link>
        </div>
        <div className="container-custom grid md:grid-cols-12 gap-10 items-center mt-6">
          <div className="md:col-span-7">
            <div className="overline mb-3">{d.tagline}</div>
            <h1 className="h1 mb-5">{d.title}</h1>
            <p className="text-lg text-[#2D4739] leading-relaxed mb-6">{d.intro}</p>
            <div className="flex gap-3">
              <a href="#get-quote" className="btn-primary">Get Free Quote <ArrowRight weight="bold" size={18}/></a>
              <a href={`tel:${CONTACT.phonePrimary}`} className="btn-outline-dark">
                <Phone weight="bold" size={18}/> Call {CONTACT.phonePrimary}
              </a>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden">
              <img src={d.image} alt={d.title} className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section-padding bg-[#F9F8F6]">
        <div className="container-custom grid md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <div className="overline mb-3">BENEFITS</div>
            <h2 className="h2 mb-6">Why {d.tagline.toLowerCase()} with Orax</h2>
            <div className="space-y-3">
              {d.benefits.map((b, i) => (
                <motion.div key={i} initial={{opacity:0,x:-20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{delay:i*0.04}} className="flex items-start gap-3">
                  <CheckCircle weight="fill" className="text-[#135934] mt-1 shrink-0" size={22}/>
                  <span className="text-[#2D4739] text-lg">{b}</span>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="overline mb-3">ESTIMATED ROI</div>
            <h2 className="h2 mb-6">A quick example</h2>
            <div className="bg-white rounded-3xl p-8 border border-[#E5E3DB]">
              <ChartLineUp weight="duotone" size={40} className="text-[#135934] mb-5"/>
              <div className="space-y-4">
                {[
                  ["Current bill", d.roi.bill],
                  ["Recommended system", d.roi.system],
                  ["Net investment", d.roi.cost],
                  ["Annual savings", d.roi.saving],
                  ["Payback period", d.roi.payback],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between pb-3 border-b border-[#E5E3DB] last:border-0">
                    <span className="text-[#5C7368]">{k}</span>
                    <span className="font-display font-bold text-xl text-[#0A1F15]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-xl mb-10">
            <div className="overline mb-3">OUR PROCESS</div>
            <h2 className="h2">How we deliver</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {d.process.map((s, i) => (
              <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.08}}>
                <div className="font-display font-black text-6xl text-[#135934]/15 leading-none mb-2">0{i+1}</div>
                <h3 className="h3 mb-2">{s.t}</h3>
                <p className="text-[#2D4739]">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="get-quote" className="section-padding bg-[#0A1F15] text-white">
        <div className="container-custom grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <h2 className="font-display font-extrabold text-4xl md:text-5xl mb-4">Ready to install {d.tagline.toLowerCase()}?</h2>
            <p className="text-white/85 text-lg">Get a free site survey and personalized quote within 24 hours.</p>
          </div>
          <div className="md:col-span-6">
            <div className="bg-white rounded-2xl p-7">
              <h3 className="h3 text-[#0A1F15] mb-4">Get your free quote</h3>
              <LeadForm source={slug}/>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
